import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useUpdateGHLCalendarLinkMutation } from '~entities/classes/api/update-ghl-calendar-link';

import { useGetGHLCalendars } from '~shared/api/get-ghl-calendars';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

import Select from '~shared/components/select/select';

interface CalendarOption {
  value: string;
  label: string;
}

interface EditBookingLinkProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string;
  classId: string;
}

export const EditBookingLink = ({
  open,
  onOpenChange,
  initialValue,
  classId,
}: EditBookingLinkProps) => {
  const [selectedCalendar, setSelectedCalendar] =
    useState<CalendarOption | null>(null);

  const { data: calendars, isLoading: isLoadingCalendars } =
    useGetGHLCalendars();

  const { mutateAsync: updateGHLCalendarLink, isPending } =
    useUpdateGHLCalendarLinkMutation();

  const calendarOptions: CalendarOption[] = useMemo(() => {
    if (!calendars || !Array.isArray(calendars)) return [];
    return calendars.map((calendar) => ({
      value: calendar.id,
      label: calendar.name,
    }));
  }, [calendars]);

  // Set initial selection when calendars are loaded
  useEffect(() => {
    if (calendars && initialValue && !selectedCalendar) {
      const initialCalendar = calendarOptions.find(
        (cal) => cal.value === initialValue,
      );
      if (initialCalendar) {
        setSelectedCalendar(initialCalendar);
      }
    }
  }, [calendars, initialValue, calendarOptions, selectedCalendar]);

  const handleSave = async () => {
    if (!selectedCalendar) {
      toast.error('Please select a calendar');
      return;
    }

    try {
      await updateGHLCalendarLink({
        classId: classId,
        ghlCalendarLink: selectedCalendar.value,
      });
      toast.success('Calendar updated', {
        description: 'The class calendar has been updated successfully.',
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update calendar:', error);
      toast.error('Failed to update calendar. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change/ Connect Class Calendar</DialogTitle>
          <DialogDescription>
            Select an Enrollio CRM calendar to connect to trial bookings.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4 py-4'>
          <Select
            value={selectedCalendar}
            onChange={(option) => setSelectedCalendar(option)}
            options={calendarOptions}
            placeholder='Select a calendar'
            isLoading={isLoadingCalendars}
            isClearable
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={isPending || isLoadingCalendars || !calendars || !selectedCalendar}
          >
            {isPending ? <Loader2 className='animate-spin' /> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
